import {
    Button,
    Card,
    Checkbox,
    Input,
    notification,
    Select,
    Spin,
    Typography,
} from 'antd';
import { addCMSMedia } from 'api/cms-media.api';
import { DestinationAreaEditor } from 'components/DestinationMediaEditor/DestinationAreaEditor';
import FormField from 'components/FormField';
import LocationSelector from 'components/LocationSelector';
import { MediaSelector } from 'components/MediaSelector';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import {
    DESTINATION_DESCRIPTION_MAX_LENGTH,
    DESTINATION_TAGLINE_MAX_LENGTH,
} from 'constants/fields-limits';
import {
    DESTINATION_MEDIA,
    DESTINATION_MEDIA_RATIO,
} from 'constants/media-sizes.constants';
import { useDestinationContentTranslation } from 'hooks/use-destination-content-translation';
import { useFormDirtyState } from 'hooks/use-form-dirty-state';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { getPolygonGeometryFromBBox } from 'utils/geojson-utils';
import styles from './DestinationEditorBasicInfo.module.css';
import {
    Destination,
    DestinationContent,
    useDestinationEditor,
} from './DestinationEditorContext';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { PurchaseStockPhotoWarningModal } from 'components/StockPhotoWarning';
import { getSourceId } from 'api/sources.api';
import { DestinationStatuses } from 'constants/destination-statuses';
import AuthorSelector from 'components/AuthorSelector';
import { DestinationUserSchema } from 'typings/destinations/DestinationUserSchema';
import { DestinationMapVisibility } from 'constants/destination-map-visibility';
import { DestinationPopularity } from 'constants/destination-popularity';

const { Text } = Typography;
const { TextArea } = Input;

const popularityOptions = [
    {
        label: '1 - Rarely explored',
        value: DestinationPopularity.RARELY_EXPLORED,
    },
    {
        label: '2 - Occasionally explored',
        value: DestinationPopularity.OCCASIONALLY_EXPLORED,
    },
    {
        label: '3 - Regularly explored',
        value: DestinationPopularity.REGULARLY_EXPLORED,
    },
    {
        label: '4 - Frequently explored',
        value: DestinationPopularity.FREQUENTLY_EXPLORED,
    },
    {
        label: '5 - Highly explored',
        value: DestinationPopularity.HIGHLY_EXPLORED,
    },
    {
        label: '6 - Intensely explored',
        value: DestinationPopularity.INTENSIVELY_EXPLORED,
    },
];

const mapVisibilityOptions = [
    {
        label: '1 - Appears last',
        value: DestinationMapVisibility.APPEARS_LAST,
    },
    {
        label: '2 - Appears at mid zoom',
        value: DestinationMapVisibility.APPEARS_AT_MID_ZOOM,
    },
    {
        label: '3 - Appears first',
        value: DestinationMapVisibility.APPEARS_FIRST,
    },
];

const defaultTagline: DestinationContent = {
    content_type: DestinationContentTypes.TAGLINE,
    body: '',
    order: 0,
    source: DestinationContentSources.MANUAL,
};

const defaultDescription: DestinationContent = {
    content_type: DestinationContentTypes.DESCRIPTION,
    body: '',
    order: 0,
    source: DestinationContentSources.MANUAL,
};

type FieldErrors = {
    tagline?: string;
    location?: string;
    name?: string;
};

type FormValues = {
    name: string | null;
    boundary_area: string | null;
    cover_image: CMSMedia | null;
    location: PSLocation | null;
    taglineContent: string;
    descriptionContent: string;
};

type DestinationEditorBasicInfoProps = Readonly<{
    onClickDiscard: () => void;
}>;

export function DestinationEditorBasicInfo({
    onClickDiscard,
}: DestinationEditorBasicInfoProps) {
    const {
        destination,
        location,
        updateDestinationField,
        updateLocation,
        updateDestination,
        saveDestination,
        saveBulkContent,
        isEditing,
        isCountry,
        isContentCurated,
        locale,
        hasContent,
    } = useDestinationEditor();
    const [isSaving, setIsSaving] = useState(false);
    const [isTogglingContentCurated, setIsTogglingContentCurated] = useState(
        false,
    );
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [imageToPurchase, setImageToPurchase] = useState<CMSMedia | null>(
        null,
    );
    const [
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
    ] = useState(false);

    const tagline = useMemo(() => {
        return (
            destination?.contents?.find(
                (content) =>
                    content.content_type === DestinationContentTypes.TAGLINE,
            ) ?? defaultTagline
        );
    }, [destination]);

    const {
        translatedContent: taglineValue,
        isTranslation,
        handleUpdateTranslationField: handleUpdateTaglineTranslationField,
    } = useDestinationContentTranslation({
        content: tagline,
        locale,
    });

    const description = useMemo(() => {
        return (
            destination?.contents?.find(
                (content) =>
                    content.content_type ===
                    DestinationContentTypes.DESCRIPTION,
            ) ?? defaultDescription
        );
    }, [destination]);

    const {
        translatedContent: descriptionValue,
        handleUpdateTranslationField: handleUpdateDescriptionTranslationField,
    } = useDestinationContentTranslation({
        content: description,
        locale,
    });

    const [taglineContent, setTaglineContent] = useState(tagline?.body ?? '');
    const [descriptionContent, setDescriptionContent] = useState(
        description?.body ?? '',
    );

    // Form values for dirty state tracking
    const formValues: FormValues = useMemo(
        () => ({
            name: destination?.name ?? '',
            boundary_area: destination?.boundary_area ?? null,
            cover_image: destination?.cover_image ?? null,
            location,
            taglineContent,
            descriptionContent,
            status: destination?.status ?? DestinationStatuses.DRAFT,
            map_visibility:
                destination?.map_visibility ??
                DestinationMapVisibility.APPEARS_LAST,
            popularity:
                destination?.popularity ??
                DestinationPopularity.RARELY_EXPLORED,
            main_editor_id: destination?.main_editor_id ?? null,
            main_editor: destination?.main_editor ?? null,
        }),
        [destination, location, taglineContent, descriptionContent],
    );

    const { isDirty, updateOriginalValues, originalValues } = useFormDirtyState(
        {
            values: formValues,
        },
    );

    const validateFields = () => {
        const errors: FieldErrors = {};

        // For locality destinations, location is required
        // For country destinations, only the name (country name) is required
        if (!isCountry && !location) {
            errors.location = 'Location is required';
        }

        if (!destination.name) {
            errors.name = isCountry
                ? 'Country name is required'
                : 'Destination name is required';
        }

        setFieldErrors(errors);

        return Object.values(errors).every((value) => !value);
    };

    const handleUpdateCoverImage = async (media: CMSMedia | null) => {
        const shutterstockSourceId = await getSourceId('shutterstock');

        if (media?.source_id === shutterstockSourceId) {
            setImageToPurchase(media);
        }

        handleUpdateField('cover_image', media ?? null);
    };

    const handleSaveCoverImage = async () => {
        const addedMedia =
            destination.cover_image && destination.cover_image.id === undefined
                ? await addCMSMedia(destination.cover_image, DESTINATION_MEDIA)
                : destination.cover_image;

        return addedMedia;
    };

    const handleSaveDestination = async (purchasedPhoto?: CMSMedia) => {
        setIsSaving(true);

        try {
            // Only include contents that have actually changed from the original values
            const contentsToSave: DestinationContentSchema[] = [];

            if (taglineContent !== originalValues?.taglineContent) {
                const currentTagline = mapContents(
                    tagline as DestinationContentSchema,
                    taglineContent,
                    DestinationContentTypes.TAGLINE,
                );
                contentsToSave.push(currentTagline);
            }

            if (descriptionContent !== originalValues?.descriptionContent) {
                const currentDescription = mapContents(
                    description as DestinationContentSchema,
                    descriptionContent,
                    DestinationContentTypes.DESCRIPTION,
                );
                contentsToSave.push(currentDescription);
            }

            const addedMedia = purchasedPhoto ?? (await handleSaveCoverImage());

            const destinationToSave = {
                ...destination,
                cover_image_id: addedMedia ? addedMedia.id : null,
                cover_image: addedMedia,
            };

            await saveDestination({
                destination: destinationToSave,
                contents: contentsToSave,
            });

            // Update original values after successful save
            updateOriginalValues();
        } catch (error) {
            console.error(error);
            notification.error({
                message: `Error saving destination: ${error}`,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (!validateFields()) {
            notification.error({
                message: 'Please fix the errors before saving',
            });
            return;
        }

        if (imageToPurchase) {
            setIsAddingStockPhotoWarningShown(true);
        } else {
            await handleSaveDestination();
        }
    };

    const handleAddPurchasedPhoto = async (purchasedPhotos: CMSMedia[]) => {
        setIsAddingStockPhotoWarningShown(false);
        setImageToPurchase(null);
        handleUpdateField('cover_image', purchasedPhotos[0]);
        await handleSaveDestination(purchasedPhotos[0]);
    };

    const handleUpdateField = useCallback(
        <T extends keyof Destination>(field: T, value: Destination[T]) => {
            updateDestinationField(field, value);
        },
        [updateDestinationField],
    );

    const mapContents = (
        content: DestinationContentSchema,
        body: string,
        type:
            | typeof DestinationContentTypes.TAGLINE
            | typeof DestinationContentTypes.DESCRIPTION,
    ) => {
        if (content.content_type !== type) {
            return content;
        }

        if (!isTranslation) {
            return {
                ...content,
                source: DestinationContentSources.MANUAL,
                body,
            };
        }

        return {
            ...content,
            source: DestinationContentSources.MANUAL,
            translations:
                type === DestinationContentTypes.TAGLINE
                    ? handleUpdateTaglineTranslationField({
                          destination_content_id: content.id ?? 0,
                          body,
                      })
                    : handleUpdateDescriptionTranslationField({
                          destination_content_id: content.id ?? 0,
                          body,
                      }),
        };
    };

    const handleUpdateTaglineOrDescription = (
        body: string,
        type:
            | typeof DestinationContentTypes.TAGLINE
            | typeof DestinationContentTypes.DESCRIPTION,
    ) => {
        if (type === DestinationContentTypes.TAGLINE && !body.trim().length) {
            setFieldErrors((prev) => ({ ...prev, tagline: undefined }));
        }

        handleUpdateField(
            'contents',
            destination?.contents?.map((content) =>
                mapContents(content, body, type),
            ),
        );
    };

    const handleToggleContentCurated = async (e: CheckboxChangeEvent) => {
        const isCurated = e.target.checked;

        setIsTogglingContentCurated(true);

        const destinationId = destination.id;

        if (!destinationId) {
            return;
        }

        try {
            // Use bulk save to update all contents at once
            const updatedContents = await saveBulkContent({
                destinationId,
                contents: (destination.contents ?? []).map((content) => {
                    const contentId = content.id > 0 ? content.id : undefined;
                    return {
                        ...content,
                        id: contentId,
                        source: isCurated
                            ? DestinationContentSources.MANUAL
                            : DestinationContentSources.AI,
                        translations: content.translations,
                    };
                }),
            });

            updateDestination({
                ...destination,
                contents: updatedContents,
            });

            notification.success({
                message: 'Content curated status updated successfully',
                key: 'content-curated-status-updated',
            });
        } finally {
            setIsTogglingContentCurated(false);
        }
    };

    const handleUpdateEditor = useCallback(
        (newEditorId: Id, newEditor: CMSUser) => {
            const destinationUser: DestinationUserSchema = {
                id: Number(newEditorId),
                first_name: newEditor.first_name,
                last_name: newEditor.last_name,
                profile_image: newEditor.google_profile_image,
            };

            handleUpdateField('main_editor_id', destinationUser.id);
            handleUpdateField('main_editor', destinationUser);
        },
        [handleUpdateField],
    );

    const handleUpdateCountryName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleUpdateField('name', value);
            // Also update the country field for country destinations
            handleUpdateField('country', value);
            if (value) {
                setFieldErrors((prev) => ({
                    ...prev,
                    name: undefined,
                }));
            }
        },
        [handleUpdateField],
    );

    const handleLocationChange = useCallback(
        (newLocation: PSLocationWithBB) => {
            if (newLocation) {
                setFieldErrors((prev) => ({
                    ...prev,
                    location: undefined,
                }));
            }
            updateLocation(newLocation);
            handleUpdateField('name', newLocation.name);
            handleUpdateField(
                'boundary_area',
                JSON.stringify(
                    getPolygonGeometryFromBBox(newLocation.boundingbox),
                ),
            );
        },
        [handleUpdateField, updateLocation],
    );

    useEffect(() => {
        setTaglineContent(taglineValue?.body ?? '');
    }, [taglineValue?.body]);

    useEffect(() => {
        setDescriptionContent(descriptionValue?.body ?? '');
    }, [descriptionValue?.body]);

    if (!destination) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <div className={styles.root}>
                <div className={styles.content}>
                    <Card
                        title={<Text strong>Basic Info</Text>}
                        extra={
                            <Checkbox
                                checked={isContentCurated}
                                onChange={handleToggleContentCurated}
                                disabled={
                                    !isEditing ||
                                    isTogglingContentCurated ||
                                    !hasContent
                                }
                            >
                                Curated content{' '}
                                {isTogglingContentCurated ? (
                                    <Spin size="small" />
                                ) : null}
                            </Checkbox>
                        }
                    >
                        {isCountry ? (
                            <FormField
                                label="Country name"
                                error={fieldErrors.name}
                            >
                                <Input
                                    value={destination.name ?? ''}
                                    onChange={handleUpdateCountryName}
                                    size="large"
                                    placeholder="Enter country name"
                                    disabled={isEditing}
                                    status={
                                        fieldErrors.name ? 'error' : undefined
                                    }
                                />
                            </FormField>
                        ) : (
                            <>
                                <FormField
                                    label="Location"
                                    error={fieldErrors.location}
                                >
                                    <LocationSelector
                                        value={location}
                                        placeholder="Search for a location"
                                        hasError={!!fieldErrors.location}
                                        onChange={handleLocationChange}
                                    />
                                </FormField>
                                <FormField
                                    label="Destination name"
                                    error={fieldErrors.name}
                                >
                                    <Input
                                        value={destination.name ?? ''}
                                        onChange={(e) =>
                                            handleUpdateField(
                                                'name',
                                                e.target.value,
                                            )
                                        }
                                        size="large"
                                        status={
                                            fieldErrors.name
                                                ? 'error'
                                                : undefined
                                        }
                                    />
                                </FormField>
                            </>
                        )}

                        <>
                            <FormField
                                label="Tagline"
                                error={fieldErrors.tagline}
                                length={taglineContent.length}
                                maxLength={DESTINATION_TAGLINE_MAX_LENGTH}
                            >
                                <Input
                                    status={
                                        fieldErrors.tagline
                                            ? 'error'
                                            : undefined
                                    }
                                    value={taglineContent}
                                    onChange={(e) =>
                                        setTaglineContent(e.target.value)
                                    }
                                    onBlur={(e) =>
                                        handleUpdateTaglineOrDescription(
                                            e.target.value,
                                            DestinationContentTypes.TAGLINE,
                                        )
                                    }
                                    size="large"
                                    placeholder="Optional"
                                />
                            </FormField>

                            <FormField
                                label="Description"
                                length={descriptionContent.length}
                                maxLength={DESTINATION_DESCRIPTION_MAX_LENGTH}
                            >
                                <TextArea
                                    className="form-field__textarea"
                                    autoSize={{ minRows: 1, maxRows: 6 }}
                                    value={descriptionContent}
                                    onChange={(e) =>
                                        setDescriptionContent(e.target.value)
                                    }
                                    onBlur={(e) => {
                                        handleUpdateTaglineOrDescription(
                                            e.target.value,
                                            DestinationContentTypes.DESCRIPTION,
                                        );
                                    }}
                                    size="large"
                                    placeholder="Optional"
                                />
                            </FormField>
                            <FormField label="Status">
                                <Select
                                    size="large"
                                    defaultValue={DestinationStatuses.DRAFT}
                                    value={destination.status}
                                    onChange={(value) =>
                                        handleUpdateField('status', value)
                                    }
                                    className={styles.statusSelect}
                                >
                                    <Select.Option
                                        value={DestinationStatuses.DRAFT}
                                    >
                                        Draft
                                    </Select.Option>
                                    <Select.Option
                                        value={DestinationStatuses.PUBLISHED}
                                    >
                                        Published
                                    </Select.Option>
                                </Select>
                            </FormField>
                            <FormField label="Editor">
                                <AuthorSelector
                                    value={destination.main_editor_id || ''}
                                    onChange={handleUpdateEditor}
                                />
                            </FormField>
                            {!isCountry && (
                                <>
                                    <FormField label="Map Visibility">
                                        <Select
                                            size="large"
                                            defaultValue={
                                                DestinationMapVisibility.APPEARS_LAST
                                            }
                                            value={destination.map_visibility}
                                            onChange={(value) =>
                                                handleUpdateField(
                                                    'map_visibility',
                                                    value,
                                                )
                                            }
                                            className={styles.statusSelect}
                                        >
                                            {mapVisibilityOptions.map(
                                                (option) => (
                                                    <Select.Option
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </Select.Option>
                                                ),
                                            )}
                                        </Select>
                                    </FormField>
                                    <FormField label="Popularity">
                                        <Select
                                            size="large"
                                            defaultValue={
                                                DestinationPopularity.RARELY_EXPLORED
                                            }
                                            value={destination.popularity}
                                            onChange={(value) =>
                                                handleUpdateField(
                                                    'popularity',
                                                    value,
                                                )
                                            }
                                            className={styles.statusSelect}
                                        >
                                            {popularityOptions.map((option) => (
                                                <Select.Option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </>
                            )}
                        </>
                    </Card>
                    {!isCountry && destination.boundary_area ? (
                        <Card title="Map" className={styles.mapCard}>
                            <DestinationAreaEditor
                                value={JSON.parse(destination.boundary_area)}
                                onChange={(newArea) => {
                                    handleUpdateField(
                                        'boundary_area',
                                        JSON.stringify(newArea),
                                    );
                                }}
                            />
                        </Card>
                    ) : null}
                </div>
                <Card title="Cover photo" className={styles.coverCard}>
                    <MediaSelector
                        initialMediaSource="UNSPLASH"
                        showCropperOnEdit={false}
                        disallowedSources={['FAVORITES']}
                        location={
                            isCountry && destination.name
                                ? {
                                      name: destination.name,
                                      detail: destination.name,
                                      fullDetail: destination.name,
                                      countryCode: '',
                                      lat: 0,
                                      lon: 0,
                                  }
                                : location
                        }
                        includeVideos={false}
                        onSelect={handleUpdateCoverImage}
                        onDelete={() => {
                            handleUpdateCoverImage(null);
                        }}
                        media={destination.cover_image ?? null}
                        ratio={DESTINATION_MEDIA_RATIO}
                    />
                </Card>
            </div>
            <div className="full-page-tabs-footer">
                <div className="max-content-width">
                    <div className="grow-full-flex"></div>
                    <Button size="large" onClick={onClickDiscard}>
                        Cancel
                    </Button>
                    <Button
                        size="large"
                        type="primary"
                        loading={isSaving}
                        disabled={!isDirty}
                        onClick={handleSave}
                    >
                        Save changes
                    </Button>
                </div>
            </div>
            {isAddingStockPhotoWarningShown && imageToPurchase ? (
                <PurchaseStockPhotoWarningModal
                    photosToPurchase={[imageToPurchase]}
                    onAdd={handleAddPurchasedPhoto}
                    onCancel={() => {
                        setIsAddingStockPhotoWarningShown(false);
                    }}
                    sizesToUpload={DESTINATION_MEDIA}
                />
            ) : null}
        </>
    );
}
